import type { ChartTokens } from "types/theme";

import { PRIMARY_PALETTES, PrimaryPaletteKey } from "./primaryPalettes";

/**
 * The categorical chart ramp — the colours a chart hands to its slices, bars
 * and lines when the data has no colour of its own.
 *
 * Derived from the selected primary palette rather than fixed, which is the
 * whole point. The old lists mixed a theme var or two into a run of hard-coded
 * hues (`CATEGORY_COLORS` was `primary-light`, violet, `success-main`, teal,
 * `warning-main`, purple), so switching from Forest to Desert repainted the
 * first slice and left the other five exactly where they were: one green wedge
 * in a chart that was otherwise the same rainbow on every theme.
 *
 * Each palette keeps to its own hue TERRITORY — Ocean runs blue through teal
 * into green, Desert runs red through orange into yellow — so a chart reads as
 * that theme's family rather than as a wheel that happens to start on the brand
 * colour. See `CHART_TERRITORY`.
 *
 * Everything here is computed in OKLCH, not HSL. HSL's lightness is a channel
 * average and not a perceived brightness — HSL yellow at 50% reads far lighter
 * than HSL blue at the same 50% — so a ramp built by holding HSL lightness
 * steady comes out visibly lumpy. OKLCH holds PERCEIVED lightness, which is
 * what "these ten colours carry equal weight" has to mean.
 */

/** How many categorical slots a chart can ask for before they repeat. */
export const CHART_SERIES_LENGTH = 10;

export type ChartScheme = "light" | "dark";

/** The band of hue one palette's charts are allowed to use. */
export interface ChartTerritory {
  /**
   * Where the family is centred, in OKLCH degrees. `"glow"` takes the hue of
   * the palette's own `glow` — its vivid brand accent.
   *
   * NOT `primary.main`: that is the near-black the shell is built from, and a
   * ramp anchored on it leads with a colour too dark to read as data. The mark
   * in the corner keeps the deep green; the chart gets the mid one.
   */
  hue: "glow" | number;
  /** How far either side of `hue` the ramp may travel, in degrees. */
  arc: readonly [number, number];
}

/**
 * Each palette's own stretch of the wheel — a TORSION, not a spread.
 *
 * Roughly 40 degrees, where a categorical palette would normally take 360. The
 * separation comes from lightness instead, which is what makes the set read as
 * one family: a chart is recognisably Ocean's or Desert's before you have read
 * a single label. The drift that remains is there because a pure fade of one
 * hue looks synthetic — real pigment shifts as it lightens, and about 20
 * degrees of it is the difference between "designed" and "generated".
 *
 * The arcs lean the way each family does: Desert opens towards yellow rather
 * than pink, Ocean towards indigo rather than green.
 */
export const CHART_TERRITORY: Record<PrimaryPaletteKey, ChartTerritory> = {
  desert: { arc: [-14, 26], hue: "glow" },
  forest: { arc: [-12, 28], hue: "glow" },
  luna: { arc: [-20, 20], hue: "glow" },
  // 262, periwinkle: `night` is the achromatic theme and its `glow` token is the
  // same green as Forest's, so left to inherit it the two drew identical
  // charts. Its own hue is what separates them — it carried a per-palette chroma
  // multiplier too, and that was a mistake: the curve is searched against a
  // single chroma model, so scaling one palette underneath it quietly moved
  // that palette's slots closer together than the search believed. The option
  // is gone with it. A theme that needs to be quieter needs its own curve, not
  // a thumb on this one.
  night: { arc: [-18, 22], hue: 262 },
  ocean: { arc: [-26, 14], hue: "glow" }
};

/**
 * The shape of the ramp, and the one place to retune it.
 *
 * `position` is where each slot sits in its palette's arc, 0 at one end and 1
 * at the other, so a slot moves with the territory instead of naming a hue.
 * Slot 0 ignores it and takes the anchor hue exactly — it is the theme colour.
 *
 * `lightness` and `chroma` are absolute OKLCH targets, listed per slot rather
 * than as a formula: inside a single hue family, lightness IS the separation. A
 * donut puts slots 3 and 4 edge to edge, and two colours a few degrees apart at
 * the same lightness are one smear at that boundary.
 */
export interface ChartSeriesCurve {
  position: readonly number[];
  lightness: Readonly<Record<ChartScheme, readonly number[]>>;
  chroma: Readonly<Record<ChartScheme, readonly number[]>>;
}

export const CHART_SERIES_CURVE: ChartSeriesCurve = {
  chroma: {
    dark: [0.15, 0.1, 0.113, 0.144, 0.076, 0.113, 0.076, 0.03, 0.03, 0.03],
    light: [0.137, 0.16, 0.115, 0.134, 0.115, 0.106, 0.035, 0.036, 0.035, 0.12]
  },
  lightness: {
    dark: [0.7, 0.87, 0.517, 0.804, 0.612, 0.491, 0.732, 0.796, 0.556, 0.443],
    light: [
      0.559, 0.741, 0.373, 0.452, 0.657, 0.708, 0.731, 0.598, 0.511, 0.446
    ]
  },
  position: [0, 0, 0, 0.031, 0.164, 1, 0, 0.912, 1, 1]
};

/**
 * SLOTS 1-3 ARE NOT LIKE THE REST. Almost every chart in the app shows three
 * categories or fewer, so those three are the palette most people ever see, and
 * they are constrained hardest: all strongly coloured, and spread as a
 * mid / light / dark trio of the family. Their worst pair sits about two and a
 * half times further apart than the ramp's overall floor.
 *
 * That constraint exists because its absence was visible. An earlier curve let
 * the search mute whichever slots it liked, and it muted slot 2 — putting a
 * desaturated sage second, so a three-segment bar led with a strong green, a
 * mustard, and a dark green. Muting is now allowed only from slot 7 back, where
 * a tinted neutral is a supporting step rather than one of the three colours on
 * every screen.
 *
 * Why these numbers and not rounder ones: they are a search result, not a hand
 * pick. The constraints are in `chartSeries.test.ts` — every slot legible on
 * its own ground, no two of the ten within 0.07 perceived distance on ANY of
 * the five palettes in either scheme, and a chroma floor so nothing sags into
 * mud. That floor matters: allowed to go grey, the search happily spent five of
 * Forest's ten slots on near-neutrals, which separates well and looks filthy.
 *
 * Retuning: change the numbers, run the tests. They will tell you if you have
 * made two slices the same colour on a palette you were not looking at.
 */

interface Oklch {
  L: number;
  C: number;
  h: number;
}

const srgbToLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

const linearToSrgb = (c: number): number =>
  c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;

/** sRGB hex -> OKLCH. Ottosson's matrices, unmodified. */
export const hexToOklch = (hex: string): Oklch => {
  const raw = hex.replace("#", "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;

  const [r, g, b] = [0, 2, 4].map((i) =>
    srgbToLinear(parseInt(full.slice(i, i + 2), 16) / 255)
  );

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  return {
    C: Math.hypot(A, B),
    h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360,
    L
  };
};

const oklchToLinearRgb = ({ C, h, L }: Oklch): number[] => {
  const rad = (h * Math.PI) / 180;
  const a = C * Math.cos(rad);
  const b = C * Math.sin(rad);

  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  ];
};

const GAMUT_EPSILON = 1e-4;
const GAMUT_STEPS = 64;
const GAMUT_FALLOFF = 0.96;

const inGamut = (rgb: readonly number[]): boolean =>
  rgb.every((c) => c >= -GAMUT_EPSILON && c <= 1 + GAMUT_EPSILON);

/**
 * OKLCH -> hex, walking chroma down until the colour fits sRGB.
 *
 * Hue and lightness are held and chroma gives way, because those are the two
 * the ramp is built on: clipping the RGB channels instead (the obvious
 * alternative) shifts hue, and a slot asked for at one end of a palette's
 * territory would land outside it.
 */
export const oklchToHex = (colour: Oklch): string => {
  let { C } = colour;

  for (let i = 0; i < GAMUT_STEPS; i++) {
    if (inGamut(oklchToLinearRgb({ ...colour, C }))) break;
    C *= GAMUT_FALLOFF;
  }

  return `#${oklchToLinearRgb({ ...colour, C })
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, linearToSrgb(v))) * 255)
        .toString(16)
        .padStart(2, "0")
    )
    .join("")
    .toUpperCase()}`;
};

/** Where a palette's family is centred, in this scheme. */
export const chartAnchorHue = (
  palette: PrimaryPaletteKey,
  scheme: ChartScheme
): number => {
  const { hue } = CHART_TERRITORY[palette];

  return hue === "glow"
    ? hexToOklch(PRIMARY_PALETTES[palette][scheme].primary.glow).h
    : hue;
};

/**
 * The ten categorical colours for one palette in one scheme.
 *
 * Slot 0 is the theme's own hue at a mid tone — the colour a reader would call
 * "the Forest green" without qualifying it — and every other slot is that hue
 * displaced within the palette's territory.
 */
export const buildChartSeries = (
  palette: PrimaryPaletteKey,
  scheme: ChartScheme
): string[] => {
  const { arc } = CHART_TERRITORY[palette];
  const anchor = chartAnchorHue(palette, scheme);
  const [from, to] = arc;

  return CHART_SERIES_CURVE.position.map((position, slot) => {
    const offset = slot === 0 ? 0 : from + position * (to - from);

    return oklchToHex({
      C: CHART_SERIES_CURVE.chroma[scheme][slot],
      h: (((anchor + offset) % 360) + 360) % 360,
      L: CHART_SERIES_CURVE.lightness[scheme][slot]
    });
  });
};

/**
 * The series as the theme carries it: one key per slot, numbered and nothing
 * else. MUI builds a variable name by joining the key path with dashes, so a
 * `series1` key nested under `series` would surface as
 * `--mui-chart-series-series1`.
 *
 * Written out rather than folded from the array so the compiler checks that the
 * ramp really does fill all ten slots.
 */
export const toChartSeriesTokens = (
  series: readonly string[]
): ChartTokens["series"] => ({
  1: series[0],
  2: series[1],
  3: series[2],
  4: series[3],
  5: series[4],
  6: series[5],
  7: series[6],
  8: series[7],
  9: series[8],
  10: series[9]
});

/**
 * The slot a chart's `index`th item takes, wrapping once the ten run out.
 *
 * Charts index from 0; the tokens count from 1.
 */
export const chartSeriesVar = (index: number): string =>
  `var(--mui-chart-series-${(index % CHART_SERIES_LENGTH) + 1})`;
